import { useLocation } from "src/router";

import { useMemo } from "react";
import { useActivateSection } from "./sections/activate";
import { useAgentsSection } from "./sections/agents";
import { useCustomerStudioSection } from "./sections/customer-studio";
import { useDecisionEnginesSection } from "./sections/decision-engine";
import { useEventCollectionSection } from "./sections/event-collection";
import { useIntelligenceSection } from "./sections/hooks/intelligence";
import { useIntegrationsSection } from "./sections/integrations";
import { useSettingsSection } from "./sections/settings";
import { NavSectionLink } from "src/components/layout/nav/nav-section";
import { useLifecycleStudioSection } from "./sections/lifecycle-studio";

import { useAdStudioSection } from "./sections/ad-studio";
import { useContextHubSection } from "./sections/context-hub";
import { useCEPSection } from "./sections/cep";

export type ActiveSectionProps = {
  openSection: string | undefined;
  activeSection: string | undefined;
};

export const useActiveSection = (): ActiveSectionProps => {
  const router = useLocation();

  const eventCollection = useEventCollectionSection();
  const decisioning = useDecisionEnginesSection();
  const intelligence = useIntelligenceSection();
  const activate = useActivateSection();
  const agents = useAgentsSection();
  const customerStudio = useCustomerStudioSection();
  const integrations = useIntegrationsSection();
  const settings = useSettingsSection();
  const lifecycleStudio = useLifecycleStudioSection();

  const adStudio = useAdStudioSection();
  const contextHub = useContextHubSection();
  const esp = useCEPSection();

  const sections = useMemo(
    () => [
      eventCollection,
      intelligence,
      activate,
      agents,
      customerStudio,
      lifecycleStudio,
      adStudio,
      contextHub,
      decisioning,
      integrations,
      esp,
      settings,
    ],
    [
      eventCollection,
      intelligence,
      activate,
      agents,
      customerStudio,
      lifecycleStudio,
      adStudio,
      contextHub,
      decisioning,
      integrations,
      esp,
      settings,
    ],
  );

  return useMemo(() => {
    const enabledSections = sections.filter((section) => section.isEnabled);

    let activeLinkIndex = 0;
    let activeSection: string | undefined;

    for (const section of enabledSections) {
      activeLinkIndex = section.links
        .filter((x): x is NavSectionLink => Boolean(x))
        .findIndex((link) =>
          isLinkActive(
            router.pathname,
            link.href,
            link.isSub,
            link.activePaths,
          ),
        );

      if (activeLinkIndex !== -1) {
        activeSection = section.title;
        break;
      }
    }

    // Open all sections only if Activate and Integrations are enabled, with the exception of settings
    if (
      enabledSections.includes(activate) &&
      enabledSections.includes(integrations) &&
      (enabledSections.length === 2 ||
        (enabledSections.includes(settings) && enabledSections.length === 3))
    ) {
      return { openSection: "all", activeSection, activeLinkIndex };
    } else {
      return { openSection: activeSection, activeSection, activeLinkIndex };
    }
  }, [router.pathname, sections, integrations, settings, activate]);
};

export const useActiveLink = ({
  href,
  isSub = false,
  activePaths,
}: {
  href: string | undefined;
  isSub?: boolean | undefined;
  activePaths?: string[];
}) => {
  const router = useLocation();
  return isLinkActive(router.pathname, href, isSub, activePaths);
};

const isLinkActive = (
  pathname: string,
  href: string | undefined,
  isSub: boolean | undefined,
  activePaths?: string[],
) => {
  const paths = pathname.split("/");
  const path = isSub ? `/${paths[2]}/${paths[3]}` : `/${paths[2]}`;
  if (path === href) return true;
  return activePaths?.includes(path) ?? false;
};
